import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import Cookies from 'js-cookie';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { ACCESS_TOKEN_KEY } from '../../constants/auth';
import { useSignIn } from '../../query/auth';
import InputField from '../fields/input-field';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Form } from '../ui/form';

const formSchema = z.object({
  email: z
    .string({ required_error: '이메일을 입력해주세요.' })
    .email('유효한 이메일 주소를 입력해주세요.'),
  password: z
    .string({ required_error: '비밀번호를 입력해주세요.' })
    .min(6, '비밀번호는 6자 이상이어야 합니다.'),
});

type FormData = z.infer<typeof formSchema>;

const SignInForm = () => {
  const navigate = useNavigate();
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { mutate: signIn, isPending: isSignIn } = useSignIn();

  const onSubmit = (data: FormData) => {
    signIn(data, {
      onSuccess: response => {
        toast.success('로그인을 완료했습니다.');
        Cookies.set(ACCESS_TOKEN_KEY, response.data?.accessToken ?? '');
        navigate('/course', { replace: true });
      },
      onError: () => {
        toast.error('로그인에 실패했습니다.');
      },
    });
  };

  return (
    <Card className="w-[500px]" data-testid="student-login-card">
      <CardHeader>
        <CardTitle>학생 로그인</CardTitle>
        <CardDescription>로그인 해주세요.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            data-testid="student-login-form"
          >
            <InputField
              control={form.control}
              name="email"
              placeholder="이메일을 입력해주세요."
              label="이메일"
              type="email"
              autoComplete="username"
              data-testid="email-input"
            />
            <InputField
              control={form.control}
              name="password"
              placeholder="비밀번호를 입력해주세요."
              label="비밀번호"
              type="password"
              autoComplete="current-password"
              data-testid="password-input"
            />

            <div className="flex w-full items-center justify-center gap-4">
              <Button type="submit" className="align-middle" disabled={isSignIn} data-testid="signin-button">
                로그인
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
      <CardFooter>
        <div className="w-full flex justify-center items-center">
          <Button variant="link" asChild data-testid="signup-link">
            <Link to={'/sign-up'}>회원가입하기</Link>
          </Button>
          <Button variant="link" asChild data-testid="forgot-password-link">
            <Link to={'/forgot-password'}>비밀번호찾기</Link>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default SignInForm;
